import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import {
  PUBLIC_BETA_NOTICE_LAST_UPDATED,
  PUBLIC_BETA_NOTICE_PARAGRAPHS,
  PUBLIC_BETA_NOTICE_SUPPORT_EMAIL,
} from '../../pages/public/publicBetaNoticeContent';
import { keepFocusInside, returnFocus } from '../LegalTerminal/Overlays/overlayFocus';
import styles from './LoginEnhanced.module.css';

interface LegalNoticeModalProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
}

export default function LegalNoticeModal({ isOpen, title, onClose }: LegalNoticeModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      keepFocusInside(dialogRef.current, event);
    };

    document.addEventListener('keydown', handleKeyDown);
    window.setTimeout(() => {
      const closeButton = dialogRef.current?.querySelector<HTMLButtonElement>('button');
      closeButton?.focus();
    }, 0);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      returnFocus({ current: previouslyFocused });
    };
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === 'undefined') return null;

  const handleOverlayPointer = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div
      className={styles.legalModalOverlay}
      onMouseDown={handleOverlayPointer}
      data-testid="legal-notice-overlay"
    >
      <div
        ref={dialogRef}
        className={styles.legalModal}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className={styles.legalModalHeader}>
          <div>
            <h2 id={titleId} className={styles.legalModalTitle}>{title}</h2>
            <p className={styles.legalModalUpdated}>Last updated: {PUBLIC_BETA_NOTICE_LAST_UPDATED}</p>
          </div>
          <button
            type="button"
            className={styles.legalModalClose}
            onClick={onClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className={styles.legalModalBody}>
          {PUBLIC_BETA_NOTICE_PARAGRAPHS.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          <p>
            Questions:{' '}
            <a href={`mailto:${PUBLIC_BETA_NOTICE_SUPPORT_EMAIL}`}>
              {PUBLIC_BETA_NOTICE_SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
